import {Component, Input} from '@angular/core';
import {CustomDialogComponent} from "../../custom-dialog/custom-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {FlagService} from "../../services/flag.service";
import {FlagType} from "../../utils/enums";
import { AddUserComponent } from '../../../core/agency/agency-users/add-user/add-user.component';
import { DetailsUserComponent } from '../../../core/agency/agency-users/details-user/details-user.component';
import { AgencyUsersService } from '../../../services/agency-users.service';
import { PermissionService } from '../../../services/permission.service';
import { AgencyPermission } from '../../utils/agency-rbac';

/**
 * Row actions of "Gestion des collaborateurs". Visibility mirrors the backend rules (which remain
 * the authority): the main admin can't be disabled/deleted, nobody disables/deletes themself.
 */
@Component({
  selector: 'app-user-action',
  templateUrl: './user-action.component.html',
  styleUrl: './user-action.component.scss'
})
export class UserActionComponent {
  @Input() data: any = undefined;
  @Input() column: any = undefined;

  busy = false;

  constructor(private dialog: MatDialog,
              private usersService: AgencyUsersService,
              private flag: FlagService,
              private permissions: PermissionService) {
  }

  /** Un compte administrateur n'est modifiable / supprimable que par un administrateur (règle du back). */
  private get touchable(): boolean {
    return this.data?.role !== 'ADMIN' || this.permissions.isAdmin;
  }

  /** « Modifier les droits d'un compte » : édition du profil / rôle / droits. */
  get canEdit(): boolean {
    return this.permissions.has(AgencyPermission.USER_MANAGE) && this.touchable;
  }

  get canDisable(): boolean {
    return this.permissions.has(AgencyPermission.USER_MANAGE) && this.touchable
      && this.data?.status === 'ACTIVE' && !this.data?.mainAccount && !this.data?.self;
  }

  get canEnable(): boolean {
    return this.permissions.has(AgencyPermission.USER_MANAGE) && this.touchable && this.data?.status === 'DISABLED';
  }

  /** Renvoyer une invitation = « Créer de nouveaux comptes ». */
  get canResend(): boolean {
    return this.permissions.has(AgencyPermission.USER_INVITE) && this.data?.status === 'PENDING';
  }

  /** « Supprimer des comptes ». */
  get canDelete(): boolean {
    return this.permissions.has(AgencyPermission.USER_DELETE) && this.touchable
      && !this.data?.mainAccount && !this.data?.self;
  }

  get hasMenu(): boolean {
    return this.canDisable || this.canEnable || this.canResend;
  }

  viewDialog(): void {
    this.dialog.open(DetailsUserComponent, {
      width: '666px',
      height: '100vh',
      position: { right: '0' },
      backdropClass: 'bg-transparent',
      data: this.data,
    });
  }

  editDialog(): void {
    const dialogRef = this.dialog.open(AddUserComponent, {
      width: '720px',
      autoFocus: false,
      data: this.data,
    });
    dialogRef.afterClosed().subscribe((changed) => {
      if (changed) {
        this.usersService.refresh();
      }
    });
  }

  resendInvitation(): void {
    this.run(this.usersService.resendInvitation(this.data.id), `Invitation renvoyée à ${this.data.email}.`);
  }

  disable(): void {
    this.confirm(
      'Désactiver ce compte ?',
      `${this.fullName()} ne pourra plus se connecter et ses sessions en cours seront immédiatement interrompues. Vous pourrez le réactiver à tout moment.`,
      'Désactiver'
    ).subscribe((ok) => {
      if (ok) {
        this.run(this.usersService.changeStatus(this.data.id, 'DISABLED'), 'Compte désactivé.');
      }
    });
  }

  enable(): void {
    this.run(this.usersService.changeStatus(this.data.id, 'ACTIVE'), 'Compte réactivé.');
  }

  deletePerson(): void {
    this.confirm(
      'Supprimer ce collaborateur ?',
      `Le compte de ${this.fullName()} sera définitivement supprimé. Cette action est irréversible (préférez la désactivation pour conserver l'historique).`,
      'Supprimer'
    ).subscribe((ok) => {
      if (ok) {
        this.run(this.usersService.remove(this.data.id), 'Collaborateur supprimé.');
      }
    });
  }

  private run(request: any, successMessage: string): void {
    if (this.busy) {
      return;
    }
    this.busy = true;
    request.subscribe({
      next: () => {
        this.busy = false;
        this.flag.displayFlag(successMessage, FlagType.success);
        this.usersService.refresh();
      },
      error: (err: any) => {
        this.busy = false;
        this.flag.displayFlag(AgencyUsersService.errorMessage(err), FlagType.error);
        this.usersService.refresh();
      },
    });
  }

  private confirm(title: string, description: string, primaryLabel: string) {
    return this.dialog.open(CustomDialogComponent, {
      data: { title, description, primaryLabel, secondaryLabel: 'Annuler' },
      maxWidth: '464px',
      autoFocus: false,
    }).afterClosed();
  }

  private fullName(): string {
    const name = `${this.data?.firstName ?? ''} ${this.data?.lastName ?? ''}`.trim();
    return name || this.data?.email || 'ce collaborateur';
  }
}
